class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Quantity/Remove redraws are performed directly by CartItems.
      if (event.source === 'cart-items') this.renderRevision = (this.renderRevision || 0) + 1;
    });
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
    this.renderRevision = (this.renderRevision || 0) + 1;
  }

  invalidateRender() {
    return this.renderRevision = (this.renderRevision || 0) + 1;
  }

  isCurrentRender(revision) {
    return this.isConnected && document.querySelector('cart-drawer') === this && revision === (this.renderRevision || 0);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, isCurrent, onOpened) {
    const revision = this.renderRevision || 0;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      if (!this.isCurrentRender(revision) || isCurrent?.() === false) return;
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
      window.pcAccountCart?.renderNotices?.();
      onOpened?.();
    });

    this.addEventListener(
      'transitionend',
      () => {
        if (!this.isCurrentRender(revision) || isCurrent?.() === false || !this.classList.contains('active')) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

  }

  close() {
    this.renderRevision = (this.renderRevision || 0) + 1;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    window.pcAccountCart?.renderNotices?.();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, openDrawer = true, allowRecovery = true, context = {}) {
    const revision = allowRecovery ? this.invalidateRender() : this.renderRevision;
    // Section rendering can fail after Shopify has already accepted the Add.
    // Validate every section before replacing any DOM; recover by GET, never by
    // repeating the mutation. Quiet account restores still fail closed.
    const sections = this.getSectionsToRender().map((section) => ({
      section,
      node: this.getSectionDOM(parsedState.sections?.[section.id], section.selector),
      target: section.selector ? document.querySelector(section.selector) : document.getElementById(section.id),
    }));
    if (sections.some(({ section, node, target }) => !node || !target ||
      (section.id === 'cart-drawer' && !node.querySelector('.drawer__inner')))) {
      if (!openDrawer || !allowRecovery) throw new Error('cart_render_failed');
      return this.refreshContents(parsedState, revision, context).catch(() => {
        if (this.isCurrentRender(revision) && context.isCurrent?.() !== false) this.showRenderRecovery(parsedState, context);
        return false;
      });
    }
    const drawerNode = sections.find(({ section }) => section.id === 'cart-drawer')?.node;
    if (drawerNode) this.classList.toggle('is-empty', !!drawerNode.querySelector('.drawer__inner-empty'));
    this.productId = parsedState.id;
    sections.forEach(({ node, target }) => {
      target.innerHTML = node.innerHTML;
    });
    window.pcAccountCart?.renderNotices?.();

    setTimeout(() => {
      if (!this.isCurrentRender(revision) || context.isCurrent?.() === false) return;
      const overlay = this.querySelector('#CartDrawer-Overlay');
      // Bind once per overlay element; a replaced overlay gets its own listener.
      if (overlay && this.boundOverlay !== overlay) {
        this.boundOverlay = overlay;
        overlay.addEventListener('click', this.close.bind(this));
      }
      if (context.outcome && context.outcome !== 'accepted') this.showRenderRecovery(parsedState, context, true, false);
      const displayed = () => {
        context.onDisplayed?.();
        if (context.recovery) this.refreshProductQuantities();
      };
      if (openDrawer) this.open(undefined, context.isCurrent, displayed);
      else displayed();
      // Re-run Nova currency conversion on the freshly injected (AED-base) markup.
      // The Section Rendering API returns shop-base AED prices. Nova only converts
      // elements once their container is visible, so a single rePeat() fired here
      // runs too early (drawer still mid-open) and prices stay AED until the next
      // cart mutation. Stagger guarded rePeat() calls across the open animation so
      // one lands after the drawer is visible. Idempotent (re-applies the chosen
      // currency); no-op if Nova is not loaded. See assets/nova-quiz-fix.js.
      const reconvertCurrency = () => {
        if (window.baCurr && typeof window.baCurr.rePeat === 'function') {
          try { window.baCurr.rePeat(); } catch (e) {}
        }
      };
      requestAnimationFrame(reconvertCurrency); // already-open re-adds convert instantly
      setTimeout(reconvertCurrency, 200);
      setTimeout(reconvertCurrency, 500); // lands after the ~300-400ms open animation
      setTimeout(reconvertCurrency, 900); // slow-device insurance
    });
    return true;
  }

  async refreshContents(parsedState, revision = this.renderRevision, context = {}) {
    const ids = this.getSectionsToRender().map((section) => section.id).join(',');
    const response = await fetch(`${routes.cart_url}?sections=${encodeURIComponent(ids)}`, {
      credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(5000),
    });
    if (!response.ok) throw new Error('cart_render_failed');
    const sections = await response.json();
    if (!this.isCurrentRender(revision) || context.isCurrent?.() === false) return false;
    return this.renderContents({ ...parsedState, sections }, true, false, { ...context, recovery: true });
  }

  refreshProductQuantities() {
    for (const info of document.querySelectorAll('product-info')) {
      if (!info.isConnected || !info.quantityInput || !info.quantityForm || info.dataset.originalSection || typeof info.fetchQuantityRules !== 'function') continue;
      try { Promise.resolve(info.fetchQuantityRules()).catch(() => {}); } catch {}
    }
  }

  async viewCart(triggeredBy, context = {}) {
    const revision = this.invalidateRender();
    const inspection = { outcome: context.outcome, onDisplayed: context.onDisplayed };
    this.open(triggeredBy);
    try {
      await this.refreshContents({}, revision, inspection);
    } catch {
      if (this.isCurrentRender(revision)) this.showRenderRecovery({}, inspection);
    }
  }

  showRenderRecovery(parsedState, context = {}, displayed = false, openDrawer = true) {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;
    this.querySelector('#pc-cart-render-recovery')?.remove();
    const notice = document.createElement('div');
    notice.id = 'pc-cart-render-recovery';
    notice.className = 'pcn-note pcn-note--recovery';
    notice.setAttribute('role', 'status');
    const message = document.createElement('p');
    message.textContent = context.outcome && context.outcome !== 'accepted'
      ? 'We could not confirm this add. Check your cart before adding again.'
      : 'Your item was added. The cart display could not refresh. Refresh the cart to see your items.';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'pcn-act';
    retry.textContent = 'Refresh cart';
    retry.addEventListener('click', () => {
      retry.disabled = true;
      const revision = this.invalidateRender();
      this.refreshContents(parsedState, revision, { ...context, isCurrent: undefined }).catch(() => {}).finally(() => { retry.disabled = false; });
    });
    const link = document.createElement('a');
    link.className = 'pcn-act';
    link.href = routes.cart_url;
    link.textContent = 'View cart';
    link.addEventListener('click', (event) => {
      if (!this.isConnected || document.querySelector('cart-drawer') !== this) return;
      event.preventDefault();
      this.viewCart(document.querySelector('#cart-icon-bubble'), context);
    });
    notice.append(message, retry, link);
    (this.querySelector('.cart-drawer__empty-content') || this.querySelector('.drawer__inner-empty') ||
      this.querySelector('.cart-drawer__body') || inner).prepend(notice);
    if (openDrawer) this.open(undefined, context.isCurrent);
    window.pcAccountCart?.renderNotices?.();
    if (this.classList.contains('active') && typeof trapFocus === 'function') trapFocus(inner, displayed ? undefined : retry);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string' || !html.trim()) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
